import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import type { MotionNoise } from "@/lib/api";
import { MAX_SCALE_SIGMA } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * The one control for motion noise, shared by the challenge forms and the dataset
 * recorder so the two are set with the same words and the same defaults.
 *
 * A switch plus two rows of sigmas: the per-tick noise, and the per-run bias that makes
 * dead reckoning drift. The sigmas stay visible when the switch is off -- greyed, not
 * hidden -- because they are stored either way and a TA comparing two challenges should be
 * able to see them without toggling.
 */
export function MotionNoiseFields({
  id,
  value,
  onChange,
  title = "Motion noise",
  description,
  className,
}: {
  id: string;
  value: MotionNoise;
  onChange: (next: MotionNoise) => void;
  title?: string;
  description?: React.ReactNode;
  className?: string;
}) {
  const set = <K extends keyof MotionNoise>(key: K, v: MotionNoise[K]) =>
    onChange({ ...value, [key]: v });

  const sigma = (
    key: keyof MotionNoise & `${string}_sigma`,
    label: string,
    step: string,
    max?: number,
  ) => {
    const v = value[key];
    return (
      <div className="grid gap-1.5">
        <Label htmlFor={`${id}-${key}`} className="text-xs">
          {label}
        </Label>
        <Input
          id={`${id}-${key}`}
          type="number"
          step={step}
          min="0"
          max={max}
          value={v}
          onChange={(e) => set(key, Number(e.target.value))}
          className="font-mono text-xs"
          aria-invalid={v < 0 || (max !== undefined && v > max) || undefined}
        />
      </div>
    );
  };

  return (
    <div className={cn("grid gap-3 rounded-md border p-3", className)}>
      <div className="flex items-start gap-3">
        <Switch
          id={`${id}-on`}
          checked={value.enabled}
          onCheckedChange={(v) => set("enabled", v)}
          className="mt-0.5"
        />
        <div className="min-w-0 flex-1">
          <Label htmlFor={`${id}-on`}>{title}</Label>
          <p className="text-muted-foreground text-xs">
            {description ??
              "Each tick's speeds are drawn around the nominal 3 m/s and 6 rad/s, so the same key presses never trace exactly the same path. Seeded per run and recorded, so any run can still be replayed."}
          </p>
        </div>
      </div>
      <div className={cn("grid gap-3", !value.enabled && "opacity-60")}>
        <div className="grid gap-1">
          <p className="text-muted-foreground text-xs">
            Per tick: each tick's speeds vary around the nominal. Averages out over a leg.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {sigma("linear_sigma", "Linear σ (m/s)", "0.05")}
            {sigma("angular_sigma", "Angular σ (rad/s)", "0.05")}
          </div>
        </div>
        <div className="grid gap-1">
          <p className="text-muted-foreground text-xs">
            Per run, drawn once and held: a wheel-radius error scales every distance, a
            wheelbase error scales every turn, an asymmetry bends every straight. This is
            what makes dead reckoning drift. Zero is off. Scales are capped at{" "}
            {MAX_SCALE_SIGMA}: at three sigma the robot must still move the right way.
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            {sigma("speed_scale_sigma", "Speed scale σ", "0.01", MAX_SCALE_SIGMA)}
            {sigma("turn_scale_sigma", "Turn scale σ", "0.01", MAX_SCALE_SIGMA)}
            {sigma("heading_bias_sigma", "Curvature σ (rad/m)", "0.005")}
          </div>
        </div>
      </div>
    </div>
  );
}
